import mapPortfolioDataFromApi from 'rtkQuery/sharespro/mapper/modules/portfolio/mapPortfolioDataFromApi';
import sharesproApi from 'rtkQuery/sharespro/sharesproApiService';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import mapPortfolioShortDataFromApi from 'rtkQuery/sharespro/mapper/modules/portfolio/mapPortfolioShortDataFromApi';
import {
  PortfolioData,
  PortfolioShortDataByPeriod,
} from 'model/modules/portfolio/portfolio';
import { CreateWithdrawalOrderRequestData } from 'rtkQuery/sharespro/types/withdrawal/createWithdrawalOrderRequestData.t';
import { WithdrawalOrder } from 'model/modules/withdrawal/withdrawalOrder.t';
import { FetchWithdrawalOrdersQueryParams } from 'rtkQuery/sharespro/types/withdrawal/fetchWithdrawalOrders.t';
import mapPaginationListFromApi from 'rtkQuery/sharespro/mapper/shared/mapPaginationListFromApi';

export const portfolioApi = sharesproApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchPortfolio: builder.query<PortfolioData, ApiFetchPortfolioQueryParams>({
      query: (params) => ({
        url: '/v3/finance/portfolio',
        method: 'GET',
        params,
        sendToken: true,
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return { account_id: queryArgs.account_id };
      },
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiFetchPortfolioResponseData>
      ) {
        return mapPortfolioDataFromApi(baseQueryReturnValue);
      },
    }),
    fetchPortfolioShort: builder.query<
      PortfolioShortDataByPeriod,
      ApiFetchPortfolioQueryParams
    >({
      query: (params) => ({
        url: '/v3/finance/portfolio/account',
        method: 'GET',
        params,
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiFetchPortfolioShortDataByPeriod>
      ) {
        return mapPortfolioShortDataFromApi(baseQueryReturnValue);
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const resp = await queryFulfilled;
        dispatch(
          portfolioApi.util.updateQueryData('fetchPortfolio', data, (draft) => {
            draft.chart = resp.data.chart;
            draft.balanceInfo = resp.data.balanceInfo;
          })
        );
      },
    }),
    createWithdrawalOrder: builder.mutation<
      WithdrawalOrder,
      CreateWithdrawalOrderRequestData
    >({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      query: ({ account_id, ...data }) => ({
        url: '/v3/finance/withdraw-order/create',
        method: 'PUT',
        sendToken: true,
        params: { account_id },
        body: {
          amount: data.amount,
          currency: data.currency,
          wallet_address: data.wallet_address,
        },
      }),
    }),
    fetchWithdrawalOrders: builder.query<
      PaginationList<WithdrawalOrder>,
      FetchWithdrawalOrdersQueryParams
    >({
      query: (params) => ({
        url: '/v3/finance/withdraw-order/list',
        method: 'GET',
        params,
        sendToken: true,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiPaginationListResponse<WithdrawalOrder>
        >
      ) {
        return mapPaginationListFromApi<WithdrawalOrder, WithdrawalOrder>(
          baseQueryReturnValue.data,
          (data) => {
            return data;
          }
        );
      },
    }),

    cancelWithdrawalOrder: builder.mutation<WithdrawalOrder, number>({
      query: (id) => ({
        url: '/v3/finance/withdraw-order/cancel',
        method: 'DELETE',
        sendToken: true,
        params: { id },
      }),
    }),
  }),
});

export const {
  useLazyFetchPortfolioShortQuery,
  useFetchPortfolioQuery,
  useCreateWithdrawalOrderMutation,
  useFetchWithdrawalOrdersQuery,
  useCancelWithdrawalOrderMutation,
} = portfolioApi;
